// "Recipe count" tab body for the Inventory panel's right-column
// tabbed section. Renders inline (no SectionCard wrapper) so the
// ManufacturingTabbedSection chrome owns the card border / padding /
// title row. Was `RecipeCountForm` pre-merge polish.
//
// Lets the operator post an opening-balance / build / adjustment
// cost entry against a single recipe, mirroring the materials
// Receive flow but for built subassemblies (e.g. "we paused work and
// counted 40 trusses at $87.50 each").
import { useEffect, useMemo, useState } from "react";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { dispatchInventoryChangelogRefresh } from "@/lib/manufacturing/inventoryChangelogRefreshEvent";
import { postManufacturingRecipeCostEntry } from "@/lib/manufacturingClient";
import { ManufacturingComboboxPicker } from "@/components/manufacturing/ManufacturingComboboxPicker";
import { inputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { MfgRecipeCostEntryType, MfgRecipeSummary } from "@/lib/manufacturing/types";

const ENTRY_TYPE_OPTIONS: Array<{ value: MfgRecipeCostEntryType; label: string; help: string }> = [
  {
    value: "opening_balance",
    label: "Opening balance",
    help: "First-time setup. Sets the starting on-hand count + WAC for this recipe.",
  },
  {
    value: "build",
    label: "Build",
    help: "Operator-posted build event. Adds the qty at this unit cost (re-WACs).",
  },
  {
    value: "adjustment",
    label: "Adjustment",
    help: "Delta correction after a physical count. Use a negative qty to reduce.",
  },
];

export function RecipeCountBody({
  recipes,
  recipesLoading,
  onPosted,
}: {
  recipes: ReadonlyArray<MfgRecipeSummary>;
  recipesLoading: boolean;
  onPosted: (msg: string) => void;
}) {
  const orgId = useScopedOrganizationId() ?? "";
  const [recipeId, setRecipeId] = useState("");
  const [entryType, setEntryType] = useState<MfgRecipeCostEntryType>("opening_balance");
  const [qty, setQty] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [notes, setNotes] = useState("");
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draftReady, setDraftReady] = useState(false);

  useEffect(() => {
    if (!orgId) {
      setDraftReady(true);
      return;
    }
    try {
      const raw = localStorage.getItem(`mfg:recipe-count-form:v1:${orgId}`);
      if (raw) {
        const d = JSON.parse(raw) as {
          recipeId?: string;
          entryType?: MfgRecipeCostEntryType;
          qty?: string;
          unitCost?: string;
          notes?: string;
        };
        if (d.recipeId) setRecipeId(d.recipeId);
        if (d.entryType) setEntryType(d.entryType);
        if (d.qty) setQty(d.qty);
        if (d.unitCost) setUnitCost(d.unitCost);
        if (d.notes) setNotes(d.notes);
      }
    } catch {
      // ignore
    }
    setDraftReady(true);
  }, [orgId]);

  useEffect(() => {
    if (!orgId || !draftReady) return;
    try {
      localStorage.setItem(
        `mfg:recipe-count-form:v1:${orgId}`,
        JSON.stringify({ recipeId, entryType, qty, unitCost, notes }),
      );
    } catch {
      // ignore
    }
  }, [draftReady, entryType, notes, orgId, qty, recipeId, unitCost]);

  const activeRecipes = useMemo(() => recipes.filter((r) => r.is_active), [recipes]);
  const recipeOptions = useMemo(
    () =>
      activeRecipes.map((r) => ({
        key: r.id,
        label: r.name,
        sublabel: r.code,
        data: r,
      })),
    [activeRecipes],
  );
  const helpText = ENTRY_TYPE_OPTIONS.find((o) => o.value === entryType)?.help ?? "";

  function reset() {
    setRecipeId("");
    setEntryType("opening_balance");
    setQty("");
    setUnitCost("");
    setNotes("");
    setError(null);
  }

  async function handleSubmit() {
    setError(null);
    if (!recipeId.trim()) {
      setError("Pick a recipe to count.");
      return;
    }
    const qtyN = parseFloat(qty);
    if (!Number.isFinite(qtyN) || qtyN === 0) {
      setError("Quantity must be a non-zero number.");
      return;
    }
    const unitCostN = parseFloat(unitCost);
    if (!Number.isFinite(unitCostN) || unitCostN < 0) {
      setError("Unit cost must be a non-negative number.");
      return;
    }
    setWorking(true);
    const r = await postManufacturingRecipeCostEntry({
      recipe_id: recipeId,
      entry_type: entryType,
      quantity: qtyN,
      unit_cost: unitCostN,
      notes,
    });
    setWorking(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    const recipeName = activeRecipes.find((rr) => rr.id === recipeId)?.name ?? "recipe";
    onPosted(`Posted ${qtyN} ${recipeName} @ $${unitCostN.toFixed(2)} (on hand: ${r.data.on_hand_qty})`);
    dispatchInventoryChangelogRefresh();
    reset();
    if (orgId) {
      try {
        localStorage.removeItem(`mfg:recipe-count-form:v1:${orgId}`);
      } catch {
        // ignore
      }
    }
  }

  return (
    <div className="space-y-3">
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {error}
        </div>
      ) : null}

      <label className="block text-xs text-gray-600 dark:text-zinc-300">
        Recipe
        <div className="mt-1">
          <ManufacturingComboboxPicker
            value={recipeId}
            options={recipeOptions}
            onPick={(option) => setRecipeId(option.key)}
            placeholder={recipesLoading ? "Loading…" : "Search recipes by name or code…"}
            disabled={recipesLoading || working}
          />
        </div>
      </label>

      <label className="block text-xs text-gray-600 dark:text-zinc-300">
        Entry type
        <select
          className={`${inputClass} mt-1`}
          value={entryType}
          onChange={(e) => setEntryType(e.target.value as MfgRecipeCostEntryType)}
        >
          {ENTRY_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <span className="mt-1 block text-[11px] text-gray-500 dark:text-zinc-400">{helpText}</span>
      </label>

      <div className="grid grid-cols-2 gap-2">
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Qty
          <input
            className={`${inputClass} mt-1`}
            type="number"
            step="any"
            placeholder="e.g. 40"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Unit cost ($)
          <input
            className={`${inputClass} mt-1`}
            type="number"
            min="0"
            step="any"
            placeholder="e.g. 87.50"
            value={unitCost}
            onChange={(e) => setUnitCost(e.target.value)}
          />
        </label>
      </div>

      <input
        className={inputClass}
        placeholder="Notes (optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />

      <button
        type="button"
        className="w-full rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
        disabled={working}
        onClick={() => void handleSubmit()}
      >
        {working ? "Posting…" : "Post recipe count"}
      </button>
    </div>
  );
}
